// ── Domain Types ────────────────────────────────────────────────────

export enum WorkflowStepType {
  ApprovalStep = 'ApprovalStep',
  AutoAssignmentStep = 'AutoAssignmentStep',
  HstProvisioningStep = 'HstProvisioningStep',
}

// ── Step Configuration ─────────────────────────────────────────────

export type AssignmentRuleType = 'Specific' | 'Team' | 'RoundRobin' | 'Skillset' | 'Workload';
export type ConditionOperator = 'AND' | 'OR';

export interface DimensionItem {
  name: string;
  value: string;
  description: string;
}

export interface ApprovalStepConfiguration {
  approverEmail: string;
  backupApproverEmail?: string;
  timeoutDuration: number;
  timeoutUnit: string;
  requireComment: boolean;
  approvalMessage: string;
  reminderIntervalHours?: number | null;
}

export interface AssignmentRule {
  type: AssignmentRuleType;
  targetUser: string;
  targetUserName: string;
  targetTeam: string;
  userPool: string[];
  requiredSkills: string[];
  maxWorkload: number;
}

export interface AutoAssignmentConfiguration {
  assignmentRule: AssignmentRule;
}

// The HST provisioning step reads everything from the ticket, so it has no settings.
export type HstProvisioningConfiguration = Record<string, unknown>;

export type StepConfiguration = ApprovalStepConfiguration | AutoAssignmentConfiguration | HstProvisioningConfiguration;

// ── Type Guards ────────────────────────────────────────────────────

// Tolerant of older payloads that may not carry every field. The backend persists
// approverEmail/approvalMessage; we accept any approval-shaped object here.
export const isApprovalConfig = (config: StepConfiguration): config is ApprovalStepConfiguration =>
  config != null &&
  ('approverEmail' in config ||
    'approvalMessage' in config);

export const isAutoAssignConfig = (config: StepConfiguration): config is AutoAssignmentConfiguration =>
  config != null && 'assignmentRule' in config;

// ── Core Domain Models ─────────────────────────────────────────────

export interface WorkflowCondition {
  field: string;
  operator: string;
  value: string;
}

export interface WorkflowStep {
  id: string;
  name: string;
  description: string;
  order: number;
  isRequired: boolean;
  timeout: number;
  stepType: WorkflowStepType;
  configuration: StepConfiguration;
}

export interface Workflow {
  id: string;
  name: string;
  description: string;
  trigger: string;
  isActive: boolean;
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
  conditions: WorkflowCondition[];
  conditionOperator: ConditionOperator;
  steps: WorkflowStep[];
}

export interface WorkflowListItem {
  id: string;
  name: string;
  description: string;
  trigger: string;
  isActive: boolean;
  createdBy: string;
  createdDate: string;
  modifiedDate?: string;
  stepCount: number;
  conditionCount: number;
}

// ── API Request / Response Types ───────────────────────────────────

export interface GetWorkflowsRequest {
  triggerType?: string;
  isActive?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

/** Shared request type for single-workflow operations (activate, deactivate, delete). */
export type WorkflowByIdRequest = Pick<Workflow, 'id'>;

export interface CreateWorkflowRequest {
  name: string;
  description: string;
  trigger: string;
  isActive: boolean;
  conditionOperator: ConditionOperator;
  conditions: WorkflowCondition[];
  steps: {
    /** Identifies the step across saves. The server gives an ID to a step that has none. */
    id?: string;
    order: number;
    stepType: string;
    name: string;
    description: string;
    /** Serialization boundary — backend accepts arbitrary JSON for step config. */
    configuration: Record<string, any>;
  }[];
}

export interface UpdateWorkflowRequest extends CreateWorkflowRequest {
  id: string;
}
